// Loading older messages.
//
// ⚠️ Nothing did. conversationsApi.messages has always accepted `beforeSeq` and
// the server has always returned `next_before_seq` — and the only caller asked
// for `{ limit: PAGE }` and never looked at the cursor. So a thread showed its
// most recent 40 messages and scrolling up found nothing: not a layout problem,
// an absence of pagination. Reported from production 2026-10-06, "when I view
// the conversation on my laptop I don't see the entire communication".
//
// The rules live here, pure, because the interesting parts are "may I ask
// again yet" and "is there more", and both are easy to get wrong in a scroll
// handler that fires dozens of times a second.

export interface HistoryState {
  /** The `before_seq` to request next, or undefined when the start is reached. */
  cursor?: number;
  /** No older messages exist. */
  done: boolean;
  /** A request is in flight. */
  loading: boolean;
}

export const initialHistory: HistoryState = { cursor: undefined, done: false, loading: false };

/** What the state becomes after a page comes back. */
export const afterPage = (nextBeforeSeq?: number): HistoryState => ({
  cursor: nextBeforeSeq,
  // ⚠️ The SERVER decides there is no more, by omitting the cursor. Inferring
  // it from a short page is wrong: a page can be short because messages were
  // deleted or filtered, and the thread would stop loading history early.
  done: !nextBeforeSeq,
  loading: false,
});

/**
 * Whether to ask for another page now.
 *
 * ⚠️ `loading` is the important one. A scroll handler fires continuously while
 * somebody holds at the top, and without this guard every frame would start
 * another request for the same page.
 */
export const shouldLoadOlder = (s: HistoryState, atTop: boolean): boolean =>
  atTop && !s.done && !s.loading && s.cursor !== undefined;

/** How close to the top counts as "at the top". */
export const TOP_THRESHOLD_PX = 120;

export const isNearTop = (scrollTop: number): boolean => scrollTop <= TOP_THRESHOLD_PX;
